import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useVenueProfile } from '@/contexts/VenueProfileContext';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryKeys';

const DEFAULT_TIER_FEATURES = new Set([
  'dashboard.view',
  'events.manage',
  'events.gm_report',
  'calendar.view',
  'settings.general',
  'team.manage',
]);

interface EntitlementsContextType {
  hasFeature: (featureKey: string) => boolean;
  tier: string | null;
  isSuperAdmin: boolean;
  isLoading: boolean;
  enabledFeatures: ReadonlySet<string>;
}

const EntitlementsContext = createContext<EntitlementsContextType | undefined>(undefined);

interface VenueEntitlementData {
  enabledFeatures: ReadonlySet<string>;
  tier: string | null;
}

async function fetchVenueEntitlements(venueId: string): Promise<VenueEntitlementData> {
  const defaultFeatures = new Set<string>(DEFAULT_TIER_FEATURES);

  // Fetch per-venue overrides and subscription tier in parallel
  const [overridesResult, subResult] = await Promise.all([
    (supabase as any)
      .from('feature_entitlements')
      .select('feature_key, enabled')
      .eq('venue_id', venueId),
    (supabase as any)
      .from('subscriptions')
      .select('tier')
      .eq('venue_id', venueId)
      .eq('status', 'active')
      .maybeSingle(),
  ]);

  // Graceful fallback when entitlement schema is not fully available yet
  if (overridesResult.error || subResult.error) {
    console.warn('Entitlements unavailable, using default features', {
      overridesError: overridesResult.error?.message,
      subscriptionsError: subResult.error?.message,
    });
    return { enabledFeatures: defaultFeatures, tier: null };
  }

  const overrides: Array<{ feature_key: string; enabled: boolean }> = overridesResult.data ?? [];
  const tier: string | null = subResult.data?.tier ?? null;

  // Start with tier features if a tier is assigned
  const features = new Set<string>();

  if (tier) {
    const { data: tierData, error: tierError } = await (supabase as any)
      .from('entitlement_tiers')
      .select('features_included')
      .eq('tier_name', tier)
      .maybeSingle();

    if (tierError) {
      console.warn('Entitlement tier lookup failed, using default features', {
        tier,
        error: tierError.message,
      });
      return { enabledFeatures: defaultFeatures, tier: null };
    }

    if (tierData?.features_included) {
      for (const f of tierData.features_included as string[]) {
        features.add(f);
      }
    }
  }

  // Apply per-venue overrides (these take precedence)
  for (const override of overrides) {
    if (override.enabled) {
      features.add(override.feature_key);
    } else {
      features.delete(override.feature_key);
    }
  }

  // No tier + no overrides usually means entitlements aren't configured yet
  if (!tier && overrides.length === 0) {
    return { enabledFeatures: defaultFeatures, tier: null };
  }

  return { enabledFeatures: features, tier };
}

export function EntitlementsProvider({ children }: { children: ReactNode }) {
  const { profile } = useVenueProfile();
  const venueId = profile?.id;
  const { isSuperAdmin, isLoading: superAdminLoading } = useSuperAdmin();

  const { data, isLoading: entitlementsLoading } = useQuery({
    queryKey: queryKeys.entitlements.forVenue(venueId ?? ''),
    queryFn: () => fetchVenueEntitlements(venueId ?? ''),
    enabled: !!venueId && !isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = superAdminLoading || (!!venueId && !isSuperAdmin && entitlementsLoading);

  const enabledFeatures = useMemo(() => {
    if (isSuperAdmin) return new Set<string>(); // Super admin bypasses — hasFeature always returns true
    return data?.enabledFeatures ?? DEFAULT_TIER_FEATURES;
  }, [isSuperAdmin, data?.enabledFeatures]);

  const value = useMemo<EntitlementsContextType>(
    () => ({
      hasFeature: (featureKey: string) => {
        if (isSuperAdmin) return true;
        return enabledFeatures.has(featureKey);
      },
      tier: data?.tier ?? null,
      isSuperAdmin,
      isLoading,
      enabledFeatures,
    }),
    [isSuperAdmin, enabledFeatures, data?.tier, isLoading],
  );

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

export function useAllEntitlements() {
  const context = useContext(EntitlementsContext);
  if (context === undefined) {
    throw new Error('useAllEntitlements must be used within an EntitlementsProvider');
  }
  return context;
}
